import { PersonalSharingPicker, type PersonalShareSelection } from "./profile/PersonalSharingPicker";
import { useOrganizationalRecordAccess } from "@/pages/organizational-record/use-organizational-record-access";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery } from "@apollo/client/react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { OrganizationalRecordSection } from "@/pages/organizational-record/OrganizationalRecordSection";
import { OrganizationalRecordToggle } from "@/pages/organizational-record/OrganizationalRecordToggle";
import { useOrganizationalRecordSection } from "@/pages/organizational-record/use-organizational-record-section";
import { AvatarCropper } from "@/components/common/AvatarCropper";
import type { CropResult } from "@/components/common/AvatarCropper";
import { BackButton } from "@/components/common/BackButton";
import { NoBackend } from "@/components/common/NoBackend";
import { Tooltip } from "@/components/common/Tooltip";
import { ServiceMenu } from "@/components/save/ServiceMenu";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { AddForm } from "@/components/design/AddForm";
import { InlineEdit } from "@/components/design/InlineEdit";
import { Section } from "@/components/design/Section";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { AddToCollectionDialog } from "@/components/save/AddToCollectionDialog";
import { CareerGraph } from "@/components/save/CareerGraph";
import { SkillsDiagram } from "@/components/save/SkillsDiagram";
import { toStoragePercent } from "@/lib/skill-levels";
import { ShareIcon } from "@/components/share/ShareItemCard";
import { useStartChat } from "@/components/chat/use-start-chat";
import type { FieldDef, FieldOption } from "@/lib/field-util";
import {
  NameUse,
  PersonalSharingDocument,
  PersonHasSharedItemsDocument,
  ViewUserProfileDocument,
} from "@/graphql/generated/operations";
import { personalItemsOfWhere } from "@/lib/search-filters";
import { cvExtractionToPreview } from "@/components/save/cv-extraction-to-preview";
import { MAX_AVATAR_BYTES, MAX_AVATAR_PIXELS, MB } from "@/lib/upload-limits";
import { MergeFieldWarning } from "@/components/save/MergeFieldWarning";
import { ProfileMergeField } from "@/components/save/ProfileMergeField";
import { ProfileMergeCollection } from "@/components/save/ProfileMergeCollection";
import { ProfileMergeSkills } from "@/components/save/ProfileMergeSkills";
import { ProfileMergeBar } from "@/components/save/ProfileMergeBar";
import {
  useProfileMergeStore,
  actionableItems,
  sectionHasActionable,
  detailDiffFor,
  DETAIL_KEY_TAB,
} from "@/stores/profile-merge-store";
import { useCvImportStore } from "@/stores/cv-import-store";
import { EMPTY_PROFILE, dataForField, type ProfileKey } from "@/lib/person-data";
import { CEFR_OPTIONS, getLanguageOptions } from "@/lib/language-options";
import { useUiStore } from "@/stores/ui-store";
import { usePendingScope } from "@/lib/pending-edits";
import { userErrorMessage } from "@/lib/user-error";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { LocalizedError, localizedMessageOf } from "@/lib/localized-error";
import { recipientIds, recipientRelations, sameRecipientIds } from "@/lib/content-sharing";
import { ContentSharingDocument } from "@/graphql/generated/operations";
import { useCertificates } from "./use-certificates";
import { useCountryStates } from "./use-country-states";
import { useEducation } from "./use-education";
import { useExperiences } from "./use-experiences";
import { useExpertiseSkills } from "./use-expertise-skills";
import { useLanguages } from "./use-languages";
import { usePerson } from "./use-person";
import { useProfileReferenceData } from "./use-profile-reference-data";
import { useProfileEntriesState, type EntryKind } from "./use-profile-entries-state";
import { EditContext, ProfileRevealContext, ShareEditContext } from "./profile/context";
import { BIRTH_DATE_PICKER_FLOOR, invalidBaseEdits, useProfileFieldWarning } from "./profile/validation";
import { ACTIVITIES_MAX_ENTRIES } from "./profile/entry-validation";
import {
  AvatarUpload,
  BasicField,
  MultiCountryField,
  SelectField,
  ShareToggle,
} from "./profile/fields";
import {
  CertificateRow,
  EducationRow,
  ExpertiseRow,
  LanguageRow,
} from "./profile/rows";
import { FavoritesSidebar, PowersSidebar } from "./profile/sidebars";
import { useToplistSharing } from "./use-toplist-sharing";
import { useAccessFeatures } from "@/auth/access-features";
import { POWERS_SOURCE_FEATURE_KEYS } from "@/auth/feature-catalog";
import { allowedScoreSources } from "@/lib/score-source";
import type {
  CertificateEntry,
  EducationEntry,
  ExpertiseEntry,
  LanguageEntry,
  EntryRow,
} from "./profile/types";

// Save · Profile - node-by-node TSX port of
// spark-workshop/apps/web/src/workshop/screens/SaveProfile.jsx (snapshot
// `workshop-design-snapshot-2026-06-02`). The visible JSX, classNames,
// layout, and helper-component shapes mirror workshop. Engineering changes
// only: TS types, named exports, tech-stack codegen'd GraphQL ops,
// per-field save-on-blur via InlineEdit's onCommit (replacing workshop's
// global PendingEdits/save-toast which is a Phase 5 dependency).
//
// Sub-sections deferred to Phase 5 (per @adr SPARK-0003-migration-boundary-design-vs-engineering@1 - heavy entries pending):
//   experiences (CareerGraph)  ·  educations  ·  expertises  ·  certifications
//   languageSkills              ·  Powers sidebar (selfAssessmentResults)
//   Favorites sidebar           ·  Avatar upload (read-only display only)
// They occupy workshop's exact layout positions with NoBackend stubs so the
// page composition stays visually consistent.

const BASIC_SHARE_FIELDS: Partial<Record<ProfileKey, string>> = {
  phone: "sharedPhone",
  email: "sharedEmail",
  city: "sharedCity",
  country: "sharedCountry",
  gender: "sharedGender",
  birthday: "sharedBirthdate",
  summary: "sharedBio",
  nationality: "sharedNationality",
  maritalStatus: "sharedMaritalStatus",
  childCount: "sharedChildCount",
  website: "sharedWebsite",
  profession: "sharedProfession",
  currentActivity: "sharedCurrentActivity",
  currentEmployer: "sharedCurrentEmployer",
  countryState: "sharedState",
  contractType: "sharedContractType",
  workingTimeModel: "sharedWorkingTimeModel",
  street: "sharedStreet",
  houseNumber: "sharedHouseNumber",
  apartment: "sharedApartmentNumber",
  zipCode: "sharedZipCode",
  drivingLicence: "sharedDriversLicense",
  currentTasks: "sharedCurrentTasks",
  miscellaneous: "sharedMiscellaneous",
  citizenship: "sharedCitizenships",
};

// Chat action next to the Pins/Community buttons when viewing another person:
// opens the existing 1:1 conversation or starts a new one (see useStartChat).
function MessageButton({ personId }: { personId: string }) {
  const intl = useIntl();
  const { hasChat, busy, startChat } = useStartChat(personId);
  const label = hasChat
    ? intl.formatMessage({ id: "profile.page.openChat", defaultMessage: "Open chat" })
    : intl.formatMessage({ id: "profile.page.startChat", defaultMessage: "Send message" });
  return (
    <Button
      id="profile-message"
      variant={hasChat ? "primary" : "secondary"}
      size="compact"
      onClick={() => void startChat()}
      disabled={busy}
      title={label}
      aria-label={label}
    >
      <ShareIcon name="comment" filled={hasChat} className="h-4 w-4" />
    </Button>
  );
}

export function ProfilePage() {
  // /p/:id sends the other-person id; /save/basics carries no params and
  // is treated as the own-profile view. Workshop's SaveProfile.jsx uses
  // the same component for both, switching the query and disabling edits.
  const intl = useIntl();
  const locale = useUiStore((s) => s.locale);
  const { id: personIdParam } = useParams();
  const viewingOther = Boolean(personIdParam);
  const organizationalRecordAccess = useOrganizationalRecordAccess();
  const canReadOrganizationalRecord = organizationalRecordAccess.canRead;
  // Which lenses of the Powers box this viewer's Roles allow, one feature Permission each. Fail-closed like
  // every feature read: none held, or still loading, shows the combined score alone.
  const { hasFeature } = useAccessFeatures();
  const scoreSources = allowedScoreSources((source) => hasFeature(POWERS_SOURCE_FEATURE_KEYS[source]));
  const navigate = useNavigate();

  // Record the profile view so it shows up under the owner's "Profilbesucher"
  // (genesis writes USER_PROFILE_READ and skips self-views). Fire-and-forget,
  // once per viewed profile.
  const [recordView] = useMutation(ViewUserProfileDocument);
  useEffect(() => {
    if (!personIdParam) return;
    void recordView({ variables: { input: { userProfileId: personIdParam } } }).catch(() => {});
  }, [personIdParam, recordView]);
  const [addTarget, setAddTarget] = useState<"pins" | "community" | null>(null);
  // Fachwissen view: list (current rows) vs diagram (level axis). Both are
  // editable (the diagram has per-level add + drag/drop), so default to the
  // modern diagram view on open for own profile and /p/:id alike.
  const [skillsMode, setSkillsMode] = useState<"list" | "diagram">("diagram");
  // Details box toggles between personal data and the HR / company snapshot
  // (the latter is the structured, searchable occupation fields — see
  // CONTEXT.md "Occupation"). Both edit the same Person; only the visible
  // field set changes.
  const [detailsMode, setDetailsMode] = useState<"personal" | "company">("personal");

  // Quick-action deep link from the home page (?add=experience|education|
  // certificate): captured once, then stripped from the URL so it doesn't
  // re-open on later navigations. AddForm keeps the form open via its own
  // state, so clearing the param doesn't close it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [addKind] = useState<string | null>(() => searchParams.get("add"));
  useEffect(() => {
    if (searchParams.has("add")) {
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Hat diese Person ueberhaupt etwas veroeffentlicht? Der Button unten fuehrt sonst auf eine
  // Seite, die nur "Keine Beitraege zu sehen" sagt, und ein Weg, der nirgendwohin fuehrt, ist
  // schlechter als ein sichtbar nicht begehbarer. Dieselbe Bedingung wie die Zielseite, sonst
  // verspricht die Probe etwas, das die Seite dann nicht zeigt.
  const sharedItemProbe = useQuery(PersonHasSharedItemsDocument, {
    variables: { input: { first: 1, where: personalItemsOfWhere(personIdParam ?? "") } },
    skip: !viewingOther || !personIdParam,
  });
  const hasSharedItems = (sharedItemProbe.data?.sharedItems.nodes.length ?? 0) > 0;

  const person = usePerson(viewingOther ? personIdParam : undefined);
  const me = person.person;
  const { expanded: organizationalRecordExpanded, sectionRef: organizationalRecordSectionRef, open: openOrganizationalRecord } =
    useOrganizationalRecordSection(me?.id, canReadOrganizationalRecord);
  const refetch = person.refetch;
  const refetchOwnAvatar = person.refetchAvatar;
  const activeAvatar = person.avatar;

  const referenceData = useProfileReferenceData();
  const countryOptions = referenceData.countries;
  const industryOptions = referenceData.industries;
  const degreeOptions = referenceData.degrees;
  const genderOptions = referenceData.genders;
  const maritalOptions = referenceData.maritalStatuses;
  const jobDepartmentOptions = referenceData.jobDepartments;
  const jobLocationOptions = referenceData.jobLocations;
  const contractTypeOptions = referenceData.contractTypes;
  const workingTimeModelOptions = referenceData.workingTimeModels;

  // Active in-line merge preview — a section with imported rows must render its
  // box even when the user has no entries there yet (Section drops children when
  // `empty`), so imports into empty sections still show in-line.
  const mergePreview = useProfileMergeStore((s) => s.preview);
  const mergeGuideKey = useProfileMergeStore((s) => s.guideKey);
  // A CV extraction hands its two-sided ProfileImportPreview here (the dialog stashed it and
  // navigated); the effect below adapts it to PreviewData and opens the in-line merge review.
  // genesis has already done the diff + reference resolution, so no lookups are needed here.
  const cvPending = useCvImportStore((s) => s.pending);
  const clearCvImport = useCvImportStore((s) => s.clear);
  // Render a saved reference value (stored as an id) as its localized label for the merge cards'
  // read-only reference/citizenship display; plain scalars pass through unchanged.
  const refLabel = (opts: readonly { value: string; label: string }[], id: string) =>
    opts.find((o) => o.value === id)?.label ?? id;
  const formatCurrentForReview = (key: string, value: string): string => {
    if (!value) return "";
    switch (key) {
      case "gender": return refLabel(genderOptions, value);
      case "maritalStatus": return refLabel(maritalOptions, value);
      case "country": return refLabel(countryOptions, value);
      case "citizenship":
        return value.split(",").map((s) => s.trim()).filter(Boolean).map((id) => refLabel(countryOptions, id)).join(", ");
      default: return value;
    }
  };

  const experiencesHook = useExperiences();
  const educationHook = useEducation();
  const certificatesHook = useCertificates();
  const languagesHook = useLanguages();
  const skillsHook = useExpertiseSkills();
  // Holds the birth date a save would commit. A ref because the entries hook is created above the
  // point where `profile` is derived, and because the guard must read the value at save time.
  const birthDateRef = useRef<string | null | undefined>(undefined);
  const getBirthDate = useCallback(() => birthDateRef.current, []);
  const contentSharing = useQuery(ContentSharingDocument, { skip: viewingOther, fetchPolicy: "network-only" });
  // The own top 10 lists; their sharing joins the SaveBar like every section's.
  const toplistSharing = useToplistSharing({
    skip: viewingOther,
    recipients: {
      list: contentSharing.data?.contentSharingRecipients ?? [],
      loading: contentSharing.loading,
      error: contentSharing.error ? userErrorMessage(intl, contentSharing.error) : undefined,
    },
  });
  const entriesState = useProfileEntriesState({
    defaultRecipients: contentSharing.data?.defaultContentSharingRecipients ?? [],
    // Read at save time: the birth date a save WOULD commit, so an entry date is checked against the
    // pending value rather than the stored one (the review resolves it the same way).
    getBirthDate,
    // Foreign profiles (viewingOther) read their entry collections from the
    // same `me` object - PersonProfile returns experiences/educations/... that
    // genesis has already filtered to what the caller may see. Mutations stay
    // gated by canEdit, so feeding the data here only affects read-only display.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    myself: (me as any) ?? null,
    hooks: {
      experience: experiencesHook,
      education: educationHook,
      expertise: skillsHook,
      certificate: certificatesHook,
      language: languagesHook,
    },
  });
  // Pending-edit overlay: null = unchanged, { remove: true } = pending
  // removal, { mimeType, content } = freshly cropped image. CropResult
  // matches AvatarCropper's output shape.
  const [cropFile, setCropFile] = useState<File | null>(null);
  // MAX_AVATAR_BYTES was declared but never enforced. It has to bite BEFORE the cropper mounts:
  // AvatarCropper re-encodes to a 512x512 JPEG, so what is persisted is always small, but decoding a
  // very large source into the crop preview is what freezes or OOM-kills the tab.
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarEdit, setAvatarEdit] = useState<
    null | { remove: true } | (CropResult & { remove?: false })
  >(null);
  const avatarUrl = avatarEdit && "content" in avatarEdit
      ? `data:${avatarEdit.mimeType};base64,${avatarEdit.content}`
      : avatarEdit?.remove
          ? null
          : activeAvatar?.content
              ? `data:${activeAvatar.mimeType || "image/jpeg"};base64,${activeAvatar.content}`
              : null;

  const [edits, setEdits] = useState<Partial<Record<ProfileKey, string>>>({});
  // Set when a save was blocked by field validation, so every offending field reveals its warning at
  // once rather than only the one the user last left. Recomputed on the next save attempt, and cleared
  // by discard; an individual field goes quiet on its own as soon as the user edits it again.
  const [revealFieldErrors, setRevealFieldErrors] = useState(false);
  const [shareEditing, setShareEditing] = useState(false);
  const [shareMounted, setShareMounted] = useState(false);
  // Keep Switch toggles in the DOM for 200ms after the user leaves
  // 'Sichtbarkeit bearbeiten' mode so the exit animation can play. The
  // setState in this effect is intentional - the alternative (computing
  // mountedness during render via a timer ref) is more code, not less,
  // for the same behaviour. Mirrors workshop's SaveProfile.jsx node 103-110.
  useEffect(() => {
    if (shareEditing) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShareMounted(true);
      return undefined;
    }
    const t = setTimeout(() => setShareMounted(false), 200);
    return () => clearTimeout(t);
  }, [shareEditing]);

  const base = me
    ? {
        // No "–" placeholder here, and no `|| ""` needed: fullName already
        // returns "" for an unknown name, matching EMPTY_PROFILE and the 30
        // sibling fields below. A dash in the DATA would be a sentinel that
        // reads as a real name to every consumer, silently defeating their
        // own empty-name fallbacks ("Diese Person" in the powers popover,
        // "Person" in AddToCollectionDialog, "?" in avatarInitials, the
        // "Avatar" alt text). The dash belongs in whatever RENDERS a missing
        // name, not in the value that gets substituted into prose.
        name: fullName(me.name),
        givenName: me.name?.given?.filter(Boolean).join(" ") ?? "",
        familyName: me.name?.family ?? "",
        role: me.jobTitle || "",
        phone: me.phone || "",
        email: me.email || "",
        city: me.city || "",
        country: me.country?.id || "",
        gender: me.gender?.id || "",
        birthday: me.birthDate || "",
        citizenship: (me.citizenships ?? []).map((c) => c?.id).filter(Boolean).join(","),
        summary: me.bio || "",
        website: me.website || "",
        nationality: me.nationality || "",
        age: me.ageInYears != null ? String(me.ageInYears) : "",
        maritalStatus: me.maritalStatus?.id || "",
        childCount: me.childCount != null ? String(me.childCount) : "",
        street: me.street || "",
        houseNumber: me.houseNumber || "",
        apartment: me.apartment || "",
        zipCode: me.zipCode || "",
        profession: me.profession || "",
        currentActivity: me.currentActivity || "",
        currentEmployer: me.currentEmployer || "",
        jobDepartment: me.jobDepartment?.id || "",
        jobLocation: me.jobLocation?.id || "",
        drivingLicence: me.drivingLicence || "",
        miscellaneous: me.miscellaneous || "",
        currentTasks: me.currentTasks || "",
        countryState: me.countryState?.id || "",
        contractType: me.contractType?.id || "",
        workingTimeModel: me.workingTimeModel?.id || "",
      }
    : EMPTY_PROFILE;
  const profile = { ...base, ...edits };
  // The summary is the one base-data field that is not a <BasicField> (it is a bare multiline
  // InlineEdit inside its own box), so its length warning is wired here instead of in the primitive.
  const summaryWarn = useProfileFieldWarning("bio", profile.summary);
  useEffect(() => {
    birthDateRef.current = profile.birthday;
  }, [profile.birthday]);
  const profileName =
    [profile.givenName, profile.familyName].filter(Boolean).join(" ") || profile.name;
  // Only PersonProfile (viewingOther) carries these collection flags; Myself
  // never does. Drives whether the +Pins/+Community buttons offer to add or
  // jump to the existing entry.
  const alreadyPinned = !!(me && "isPinned" in me && me.isPinned);
  const alreadyContact = !!(me && "isContact" in me && me.isContact);

  // The viewed person's own powers, for the foreign-profile sidebar (genesis#96).
  // Only PersonProfile carries the field, and genesis returns null when the
  // caller may not see them — so `undefined` here means "own profile, let the
  // sidebar load the principal's own powers" while `null` means "withheld".
  const foreignPowerLevels = useMemo(() => {
    if (!viewingOther || !me || !("powersEvaluationResults" in me)) {
      return undefined;
    }
    if (me.powersEvaluationResults == null) {
      return null;
    }
    return new Map<string, number | null | undefined>(
      me.powersEvaluationResults.map((r) => [String(r.element), r.result]),
    );
  }, [viewingOther, me]);

  // Smart analysis for the same person. On real data this is usually the only populated source, since
  // profiles are filled by CV extraction rather than by anyone completing a self-assessment.
  const foreignSmartLevels = useMemo(() => {
    if (!viewingOther || !me || !("smartAnalysisResults" in me)) {
      return undefined;
    }
    if (me.smartAnalysisResults == null) {
      return null;
    }
    return new Map<string, number | null | undefined>(
      me.smartAnalysisResults.map((r) => [String(r.element), r.score]),
    );
  }, [viewingOther, me]);
  const countryStateOptions: FieldOption[] = useCountryStates(profile.country).states;

  const sharingQuery = useQuery(PersonalSharingDocument, { skip: viewingOther, fetchPolicy: "network-only" });
  const sharingError = sharingQuery.error ? userErrorMessage(intl, sharingQuery.error) : undefined;
  const sharedAttrs = (sharingQuery.data?.myPersonalSharing.attributes ?? {}) as Record<string, string[] | null | undefined>;
  const sharingRecipients = [...new Map([
    ...(sharingQuery.data?.myPersonalSharing.recipients ?? []),
    ...(sharingQuery.data?.personalSharingRecipients ?? []),
  ].map((group) => [group.id, group])).values()];

  const update = (key: ProfileKey) => (val: string) =>
    setEdits((e) => ({ ...e, [key]: val }));

  // Per workshop: changed = entries that actually differ from the base value
  // (so reverting to the original via Esc drops the change without a save).
  const changed: Partial<Record<ProfileKey, string>> = {};
  for (const [k, v] of Object.entries(edits) as Array<[ProfileKey, string]>) {
    if (v !== (base[k] ?? "")) changed[k] = v;
  }

  const [sharedEdits, setSharedEdits] = useState<Record<string, string[]>>({});
  const [allGroupsEdits, setAllGroupsEdits] = useState<Record<string, boolean>>({});
  const savedAllGroups = sharingQuery.data?.myPersonalSharing.allGroupsAttributes ?? [];
  const isAllGroups = (attribute: string) => allGroupsEdits[attribute] ?? savedAllGroups.includes(attribute);
  const allGroupsDirty = Object.entries(allGroupsEdits).some(([attribute, enabled]) => enabled !== savedAllGroups.includes(attribute));
  const setAttributeAllGroups = (attribute: string, enabled: boolean, also?: string) => {
    setProfileGroupMode(null);
    setAllGroupsEdits((previous) => ({ ...previous, [attribute]: enabled, ...(also ? { [also]: enabled } : {}) }));
    if (!enabled) setSharedEdits((previous) => ({ ...previous, [attribute]: [], ...(also ? { [also]: [] } : {}) }));
  };
  const [profileGroupMode, setProfileGroupMode] = useState<"all" | "custom" | null>(null);
  const selection = (attribute: string, also?: string): PersonalShareSelection => ({
    allGroups: { value: isAllGroups(attribute), onChange: (value) => setAttributeAllGroups(attribute, value, also) },
    ids: sharedEdits[attribute] ?? sharedAttrs[attribute] ?? [],
    sameAsBaseline: !profileSharingMixed && sameRecipientIds(sharedEdits[attribute] ?? sharedAttrs[attribute] ?? [], profileCommonIds),
    recipients: sharingRecipients,
    loading: sharingQuery.loading,
    error: sharingError,
    onChange: (ids) => { setAttributeAllGroups(attribute, false, also); setSharedEdits((previous) => ({ ...previous, [attribute]: ids, ...(also ? { [also]: ids } : {}) })); },
  });
  const isBasicShared = (key: ProfileKey) => selection(BASIC_SHARE_FIELDS[key] ?? "", key === "birthday" ? "sharedAge" : undefined);
  const POWERS_SHARE_ATTR = "sharedPowers";

  const changedShared: Record<string, string[]> = {};
  for (const [attr, v] of Object.entries(sharedEdits)) {
    if (!sameRecipientIds(v, sharedAttrs[attr] ?? []) || isAllGroups(attr) !== savedAllGroups.includes(attr)) changedShared[attr] = v;
  }

  // === Entry collections (educations / experiences / expertises / certs / langs)
  // @adr SPARK-0005-unified-deferred-save-bar@1: every Entry aggregate now joins the page-level SaveBar. The
  // hook holds three overlays per kind (edits, drafts, errors) and exposes
  // a `save()` that commits entry mutations sequentially.
  const { patchEntry, cancelEntry, addDraft, markForDelete } = entriesState;
  const draftIds = entriesState.draftIds;
  const entryErrors = entriesState.errors;

  // Rows arrive from the orchestrator in UI shape (DateInstant → string,
  // country relation → id, activities array → "\n"-joined string,
  // sharedWith already contains Group metadata); see normalizeSavedRow in
  // use-profile-entries-state.ts. Nothing more to massage here. The
  // concrete row-interface casts narrow MergedEntries[kind] (Record-typed
  // for orchestrator-side flexibility) back to the field-typed shapes the
  // row components consume.
  const experiences = entriesState.entries.experience;
  const educations = entriesState.entries.education as unknown as EducationEntry[];
  const expertise = entriesState.entries.expertise as unknown as ExpertiseEntry[];
  const certs = entriesState.entries.certificate as unknown as CertificateEntry[];
  const langs = entriesState.entries.language as unknown as LanguageEntry[];

  const experienceRows = experiences as unknown as EntryRow[];
  const entrySections: ReadonlyArray<[EntryKind, readonly EntryRow[]]> = [
    ["experience", experienceRows], ["education", educations], ["expertise", expertise],
    ["certificate", certs], ["language", langs],
  ];
  const contentRecipients = new Map<string, { id: string; name: string; eligible: boolean }>();
  for (const group of [
    ...entrySections.flatMap(([, rows]) => rows.flatMap((row) => row.sharedWith ?? [])),
    ...(contentSharing.data?.contentSharingRecipients ?? []),
  ]) {
    if (group.id) contentRecipients.set(group.id, { id: group.id, name: group.name ?? group.id,
      eligible: group.eligible === true });
  }
  const commonRecipients = (rows: readonly EntryRow[]) => rows.length === 0 ? [] :
    recipientIds(rows[0]?.sharedWith).filter((id) => rows.every((row) => recipientIds(row.sharedWith).includes(id)));
  const entryPicker = (sections: typeof entrySections, id: string) => {
    const rows = sections.flatMap(([, entries]) => [...entries]);
    const ids = commonRecipients(rows);
    return <PersonalSharingPicker countOnly id={id}
      allGroups={{ value: rows.length > 0 && rows.every((row) => row.allGroups === true), onChange: (value) => {
        setProfileGroupMode(null);
        sections.forEach(([kind, entries]) => entries.forEach((entry) => patchEntry(kind, entry.id, {
          allGroups: value, publiclyReadable: false, ...(!value ? { sharedWith: [] } : {}),
        })));
      } }} selection={{
      sameAsBaseline: !profileSharingMixed && rows.every((row) => row.publiclyReadable !== true && sameRecipientIds(row.sharedWith, profileCommonIds)),
      displayCount: new Set(rows.flatMap((row) => row.publiclyReadable === true ? [...contentRecipients.values()].filter((group) => group.eligible).map((group) => group.id) : recipientIds(row.sharedWith))).size,
      ids, recipients: [...contentRecipients.values()], loading: contentSharing.loading,
      error: contentSharing.error ? userErrorMessage(intl, contentSharing.error) : undefined,
      mixed: rows.some((row) => !sameRecipientIds(row.sharedWith, ids)) || rows.some((row) => row.allGroups === true) && rows.some((row) => row.allGroups !== true),
      onChange: (selected) => { setProfileGroupMode(null); sections.forEach(([kind, entries]) => entries.forEach((entry) =>
        patchEntry(kind, entry.id, { allGroups: false, sharedWith: selected.map((id) => contentRecipients.get(id) ?? { id }) }))); },
    }} publication={{ mixed: rows.some((row) => row.publiclyReadable === true) && rows.some((row) => row.publiclyReadable !== true),
      value: rows.length > 0 && rows.every((row) => row.publiclyReadable === true),
      onChange: (value) => { setProfileGroupMode(null); sections.forEach(([kind, entries]) => entries.forEach((entry) =>
        patchEntry(kind, entry.id, { publiclyReadable: value, allGroups: false }))); },
    }} />;
  };

  const profileShareAttributes = [...new Set([...Object.values(BASIC_SHARE_FIELDS), "sharedAge", POWERS_SHARE_ATTR])];
  const allEntryRows = entrySections.flatMap(([, rows]) => [...rows]);
  const profileSelections = [
    ...profileShareAttributes.map((attribute) => sharedEdits[attribute] ?? sharedAttrs[attribute] ?? []),
    ...allEntryRows.map((row) => recipientIds(row.sharedWith)),
  ];
  const profileCommonIds = (profileSelections[0] ?? []).filter((id) => profileSelections.every((ids) => ids.includes(id)));
  const profileSharingMixed = profileSelections.some((ids) => !sameRecipientIds(ids, profileCommonIds))
    || allEntryRows.some((row) => row.publiclyReadable === true);
  const powersShared = selection(POWERS_SHARE_ATTR);
  const profileRecipients = [...new Map([...sharingRecipients, ...contentRecipients.values()].map((group) => [group.id, {
    ...group, eligible: sharingRecipients.some((item) => item.id === group.id && item.eligible)
      && contentRecipients.get(group.id)?.eligible === true,
  }])).values()];
  const allProfileGroupIds = profileRecipients.filter((group) => group.eligible).map((group) => group.id);
  const sharesWithAllGroups = profileShareAttributes.every(isAllGroups)
    && allEntryRows.every((row) => row.allGroups === true);
  const setProfileGroups = (ids: string[], allGroups = false) => {
    setAllGroupsEdits((previous) => ({ ...previous, ...Object.fromEntries(profileShareAttributes.map((attribute) => [attribute, allGroups])) }));
    setSharedEdits((previous) => ({ ...previous, ...Object.fromEntries(profileShareAttributes.map((attribute) => [attribute, ids])) }));
    entrySections.forEach(([kind, rows]) => rows.forEach((entry) => patchEntry(kind, entry.id, {
      sharedWith: ids.map((id) => contentRecipients.get(id) ?? { id }), publiclyReadable: false, allGroups,
    })));
  };
  const profileSharingPicker = <PersonalSharingPicker inline id="profile-visibility-all"
    allGroups={{ value: profileGroupMode === "all" || (profileGroupMode === null && sharesWithAllGroups),
      onChange: (value) => {
        setProfileGroupMode(value ? "all" : "custom");
        setProfileGroups(value ? [...new Set([...profileCommonIds, ...allProfileGroupIds])] : [], value);
      },
    }} selection={{
      ids: profileCommonIds, recipients: profileRecipients, mixed: profileSharingMixed,
      loading: sharingQuery.loading || contentSharing.loading,
      error: sharingError || (contentSharing.error ? userErrorMessage(intl, contentSharing.error) : undefined),
      onChange: (ids) => { setProfileGroupMode("custom"); setProfileGroups(ids); },
    }} />;

  const sectionTitle = {
    experience: intl.formatMessage({ id: "profile.page.section.experience", defaultMessage: "Work experience" }),
    education: intl.formatMessage({ id: "profile.page.section.education", defaultMessage: "Education" }),
    expertise: intl.formatMessage({ id: "profile.page.section.expertise", defaultMessage: "Expertise" }),
    certificates: intl.formatMessage({ id: "profile.page.section.certificates", defaultMessage: "Certificates" }),
  };
  const sectionShareToggle = (kind: EntryKind, rows: readonly EntryRow[]) =>
    rows.length > 0 && shareEditing ? entryPicker([[kind, rows]], `profile-sectionshare-${kind}`) : null;
  const sectionHeading = (kind: EntryKind, rows: readonly EntryRow[], title: string) => (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      {title}
      <span className="flex items-center font-normal leading-normal empty:hidden">
        {sectionShareToggle(kind, rows)}
      </span>
    </span>
  );
  const addSectionDraft = (kind: EntryKind, rows: readonly EntryRow[], payload: Record<string, unknown>) => {
    const ids = rows.length ? commonRecipients(rows)
      : !profileSharingMixed ? profileCommonIds : recipientIds(contentSharing.data?.defaultContentSharingRecipients);
    const allGroups = payload.sharedPublic !== false && (rows.length
      ? rows.every((row) => row.allGroups === true) : sharesWithAllGroups);
    addDraft(kind, { ...payload, sharedWith: recipientRelations(ids), allGroups,
      publiclyReadable: !allGroups && payload.sharedPublic !== false, ...(payload.sharedPublic === false ? { sharedWith: [] } : {}) });
  };

  const canUpdateProfile = me?.canUpdate === true;
  const canEdit = canUpdateProfile && !viewingOther;
  const canAddEntries = canEdit && !!contentSharing.data && !contentSharing.loading && !contentSharing.error;

  // The in-line LinkedIn merge review is an editing surface: while a preview
  // is active in the (global) merge store, its cards must only appear where
  // editing is possible — never on another person's profile.
  const reviewPreview = canEdit ? mergePreview : null;
  // The guided merge review walks the merge cards in page order; fields inside
  // the Details box live in one of its two tabs, so when the guide's active
  // step is such a field, switch the tab for it — the guide must never point
  // at an unmounted card. Manual tab browsing is untouched (the effect only
  // reruns when the active step changes).
  useEffect(() => {
    if (!canEdit) return;
    // Own-property lookup: guideKey is server-derived (a field key or rowKey),
    // so a plain index could return an inherited Object.prototype member.
    const tab =
      mergeGuideKey && Object.hasOwn(DETAIL_KEY_TAB, mergeGuideKey)
        ? DETAIL_KEY_TAB[mergeGuideKey]
        : undefined;
    // Intentional setState-in-effect: syncing the tab to the (external) merge
    // store's active step, keyed on that step alone so manual tab browsing
    // stays untouched. Mirrors the shareMounted effect above.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (tab) setDetailsMode(tab);
  }, [mergeGuideKey, canEdit]);

  // Pick up a pending CV extraction: adapt the genesis two-sided preview to PreviewData and open
  // the SAME in-line guided review the LinkedIn import uses, then clear the hand-off so it runs
  // once. Only open a review when there is something actionable (a CV with nothing new consumes the
  // hand-off silently rather than starting an empty review). Own, editable profile only.
  useEffect(() => {
    if (!canEdit || !cvPending) return;
    const preview = cvExtractionToPreview(cvPending.fragment);
    if (actionableItems(preview).length > 0) {
      useProfileMergeStore.getState().start(preview);
    }
    clearCvImport();
  }, [canEdit, cvPending, clearCvImport]);

  // Field definitions reused as addFields (AddForm) AND inline-edit forms.
  // `pairWithNext: true` lays the field next to the following one in a 2-col
  // row at `@sm:` and above (stacked on narrow containers — see @adr SPARK-0020-container-queries-as-the-responsive-primitive-inside-main@1).
  // Used for short
  // visually-related pairs like Arbeitgeber+Abteilung and Land+Ort.
  // `id` on each field stamps a stable, locale-independent DOM hook for e2e
  // tests (spark-validation), reused across the add form and the inline edit
  // panel (never both mounted at once). month-year ids fan out to
  // `${id}-month` / `${id}-year`; the string-list id lands on its container.
  // A language the person already has must not be offered again. genesis deliberately UPSERTS a
  // duplicate tag (unique_person_tag would otherwise be violated), which overwrites the existing row's
  // CEFR and native flag, resets self_assessed and returns the EXISTING row's id — so the save reported
  // success for a row that had silently merged away. Preventing the choice is the fix; the constraint
  // is not something the client should be provoking.
  const takenLanguageTags = new Set(
    (entriesState.entries.language ?? []).map((l) => String((l as { languageTag?: unknown }).languageTag ?? "")),
  );
  // Hoisted: the full list was rebuilt and spread once per language row per render. The spread was
  // pointless too - filter already returns a new array.
  const allLanguageOptions = getLanguageOptions(locale);
  const availableLanguageOptions = allLanguageOptions.filter((o) => !takenLanguageTags.has(o.value));

  const experienceFields: readonly FieldDef[] = [
    { key: "jobTitle", id: "experience-jobTitle", label: intl.formatMessage({ id: "profile.page.field.jobTitle.label", defaultMessage: "Job title" }), type: "text", required: true, placeholder: intl.formatMessage({ id: "profile.page.field.jobTitle.placeholder", defaultMessage: "e.g. Software developer" }) },
    { key: "employer", id: "experience-employer", label: intl.formatMessage({ id: "profile.page.field.employer.label", defaultMessage: "Employer" }), type: "text", placeholder: intl.formatMessage({ id: "profile.page.field.employer.placeholder", defaultMessage: "e.g. Smycles GmbH" }), pairWithNext: true },
    { key: "industry", id: "experience-industry", label: intl.formatMessage({ id: "profile.page.field.industry", defaultMessage: "Industry" }), type: "select", options: industryOptions },
    { key: "department", id: "experience-department", label: intl.formatMessage({ id: "profile.page.field.department", defaultMessage: "Department" }), type: "text" },
    { key: "country", id: "experience-country", label: intl.formatMessage({ id: "profile.page.field.country", defaultMessage: "Country" }), type: "select", options: countryOptions, pairWithNext: true },
    { key: "location", id: "experience-location", label: intl.formatMessage({ id: "profile.page.field.location", defaultMessage: "Location" }), type: "text" },
    { key: "startDate", id: "experience-startDate", label: intl.formatMessage({ id: "profile.page.field.from", defaultMessage: "From" }), type: "month-year", required: true },
    { key: "endDate", id: "experience-endDate", label: intl.formatMessage({ id: "profile.page.field.to", defaultMessage: "To" }), type: "month-year" },
    { key: "activities", id: "experience-activities", label: intl.formatMessage({ id: "profile.page.field.activities.label", defaultMessage: "Main tasks" }), type: "string-list", placeholder: intl.formatMessage({ id: "profile.page.field.activities.placeholder", defaultMessage: "e.g. Code reviews" }), suggest: "activity", maxEntries: ACTIVITIES_MAX_ENTRIES },
  ];
  // `id` mirrors experienceFields above: a stable, locale-independent DOM hook
  // for e2e tests (spark-validation), reused across the add form and the inline
  // edit row (never both mounted at once in the tests). month-year ids fan out
  // to `${id}-month` / `${id}-year`.
  const educationFields: readonly FieldDef[] = [
    { key: "field", id: "education-field", label: intl.formatMessage({ id: "profile.page.field.field.label", defaultMessage: "Field of study" }), type: "text", required: true, placeholder: intl.formatMessage({ id: "profile.page.field.field.placeholder", defaultMessage: "e.g. Computer science" }) },
    {
      key: "degree",
      id: "education-degree",
      label: intl.formatMessage({ id: "profile.page.field.degree.label", defaultMessage: "Degree" }),
      type: "select",
      selectKind: "degree",
      options: degreeOptions,
      placeholder: intl.formatMessage({
        id: "profile.page.field.degree.placeholder",
        defaultMessage: "Choose degree",
      }),
    },
    { key: "institution", id: "education-institution", label: intl.formatMessage({ id: "profile.page.field.institution", defaultMessage: "Educational institution" }), type: "text" },
    { key: "country", id: "education-country", label: intl.formatMessage({ id: "profile.page.field.country", defaultMessage: "Country" }), type: "select", options: countryOptions, pairWithNext: true },
    { key: "city", id: "education-city", label: intl.formatMessage({ id: "profile.page.field.location", defaultMessage: "Location" }), type: "text" },
    { key: "startDate", id: "education-startDate", label: intl.formatMessage({ id: "profile.page.field.from", defaultMessage: "From" }), type: "month-year", required: true },
    { key: "endDate", id: "education-endDate", label: intl.formatMessage({ id: "profile.page.field.to", defaultMessage: "To" }), type: "month-year" },
  ];
  // `id` mirrors experienceFields/educationFields above: a stable,
  // locale-independent DOM hook for e2e tests (spark-validation), reused across
  // the add form and the inline edit row (never both mounted at once in the
  // tests). The month-year id fans out to `${id}-month` / `${id}-year`.
  const certificateFields: readonly FieldDef[] = [
    { key: "name", id: "certificate-name", label: intl.formatMessage({ id: "profile.page.field.certificateName.label", defaultMessage: "Name" }), type: "text", required: true, placeholder: intl.formatMessage({ id: "profile.page.field.certificateName.placeholder", defaultMessage: "e.g. AWS Solutions Architect" }) },
    { key: "date", id: "certificate-date", label: intl.formatMessage({ id: "profile.page.field.issueDate", defaultMessage: "Issue date" }), type: "month-year" },
  ];
  // `id` mirrors the other entry forms above: a stable, locale-independent DOM
  // hook for e2e tests (spark-validation). It threads through both the Classic
  // list row and the diagram's inline editor (only one form is ever open at once);
  // the skill-level scale derives one id per level from it.
  const expertiseFields: readonly FieldDef[] = [
    { key: "description", id: "expertise-description", label: intl.formatMessage({ id: "profile.page.field.expertiseName.label", defaultMessage: "Name" }), type: "text", required: true, placeholder: intl.formatMessage({ id: "profile.page.field.expertiseName.placeholder", defaultMessage: "e.g. Kubernetes" }) },
    { key: "level", id: "expertise-level", label: intl.formatMessage({ id: "profile.page.field.level", defaultMessage: "Level" }), type: "skill", hideLabel: true },
  ];
  const languageFields: readonly FieldDef[] = [
    { key: "languageTag", id: "language-tag", label: intl.formatMessage({ id: "profile.page.field.language.label", defaultMessage: "Language" }), type: "select", options: availableLanguageOptions, placeholder: intl.formatMessage({ id: "profile.page.field.language.placeholder", defaultMessage: "Choose language" }), required: true },
    // A foreign language needs a CEFR level; a native speaker doesn't (SMY-0050).
    { key: "cefr", id: "language-cefr", label: intl.formatMessage({ id: "profile.page.field.cefr", defaultMessage: "Level (CEFR)" }), type: "select", options: [...CEFR_OPTIONS], requiredWhen: (e) => !e.nativeSpeaker },
    { key: "nativeSpeaker", id: "language-native", label: intl.formatMessage({ id: "profile.page.field.nativeSpeaker.label", defaultMessage: "Native language" }), type: "bool", placeholder: intl.formatMessage({ id: "profile.page.field.nativeSpeaker.placeholder", defaultMessage: "Native language" }) },
  ];

  /**
   * The language descriptor for one existing row: taken tags hidden, except the row's own.
   *
   * Declared AFTER languageFields, which it closes over. The per-row filter stays - each row's allowed
   * set differs by its own tag - but it now reads the hoisted list instead of rebuilding it.
   */
  const languageFieldsFor = (ownTag: string): readonly FieldDef[] =>
    languageFields.map((f) =>
      f.key === "languageTag"
        ? { ...f, options: allLanguageOptions.filter((o) => o.value === ownTag || !takenLanguageTags.has(o.value)) }
        : f,
    );

  // SaveBar owns every commit on this page (@adr SPARK-0005-unified-deferred-save-bar@1): the Person aggregate
  // (scalars, personal Group consent, avatar) AND every Entry aggregate (creates,
  // updates, deletes). Mutations run sequentially because they share person search-index
  // and access-segment locks in Genesis. Overlay state updates per outcome; if any
  // mutation failed it throws an aggregated error so the SaveBar surfaces
  // it. Successes drop their overlay slice; failures keep theirs so the
  // user can fix and retry.
  const personDirty =
    Object.keys(changed).length > 0 ||
    Object.keys(changedShared).length > 0 || allGroupsDirty ||
    avatarEdit != null;

  const buildPersonData = (): Record<string, unknown> | null => {
    if (!me) return null;
    const personData: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(changed) as Array<[ProfileKey, string]>) {
      if (k === "givenName" || k === "familyName" || k === "name") continue;
      Object.assign(personData, dataForField(k, v));
    }
    if ("givenName" in changed || "familyName" in changed) {
      const given = profile.givenName.trim()
        ? profile.givenName.trim().split(/\s+/)
        : [];
      personData.name = {
        given,
        family: profile.familyName.trim() || null,
        prefix: me.name?.prefix ?? null,
        suffix: me.name?.suffix ?? null,
        use: NameUse.Usual,
      };
    }
    if (avatarEdit) {
      personData.avatar = avatarEdit.remove
        ? null
        : { fileName: "avatar.jpg", mimeType: avatarEdit.mimeType, content: avatarEdit.content };
    }
    return Object.keys(personData).length > 0 ? personData : null;
  };

  const dirty = (canUpdateProfile && personDirty) || (canEdit && (entriesState.dirty || toplistSharing.dirty));

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): whose profile is open and its shape. Own profile
  // is the user's own data; when viewing another person we surface only their public display name.
  useAssistantContext(
    me
      ? [
          {
            description: viewingOther
              ? "another person's profile the user is viewing"
              : "the user's own profile page (inline-editable)",
            value:
              `${profileName || "unnamed"}` +
              (profile.role ? `, ${profile.role}` : "") +
              (profile.city ? `, ${profile.city}` : "") +
              (viewingOther
                ? ""
                : `; CV entries — ${experiences.length} experiences, ${educations.length} educations, ` +
                  `${expertise.length} expertise, ${certs.length} certificates, ${langs.length} languages` +
                  (dirty ? "; has unsaved edits" : "")),
          },
        ]
      : null,
  );

  // Guided tour (/tour): only on the user's own profile — no tour when viewing
  // another person (/p/:id).
  useAssistantTour(
    me && !viewingOther
      ? {
          label: intl.formatMessage({
            id: "profile.tour.label",
            defaultMessage: "Save Profile",
          }),
          summary: intl.formatMessage(
            {
              id: "profile.tour.summary",
              defaultMessage:
                "This is your own profile with {count} CV entries. Almost every field is editable right on the page.",
            },
            {
              count:
                experiences.length + educations.length + expertise.length + certs.length + langs.length,
            },
          ),
          stops: [
            {
              anchor: "save.profile.card",
              title: intl.formatMessage({ id: "profile.tour.card.title", defaultMessage: "Your business card" }),
              description: intl.formatMessage(
                {
                  id: "profile.tour.card.desc",
                  defaultMessage:
                    "Click any field (name, phone, email, country, city) to edit it in place; changes are collected and saved via the save bar.{missing}",
                },
                {
                  missing:
                    [profile.phone, profile.email, profile.city].filter(Boolean).length < 3
                      ? " " +
                        intl.formatMessage({
                          id: "profile.tour.card.missingHint",
                          defaultMessage: "Some of your contact fields are still empty.",
                        })
                      : "",
                },
              ),
            },
            {
              anchor: "save.profile.about",
              title: intl.formatMessage({
                id: "profile.tour.about.title",
                defaultMessage: "Summary, details and languages",
              }),
              description: intl.formatMessage(
                {
                  id: "profile.tour.about.desc",
                  defaultMessage:
                    "Your summary text, your personal and company details and your {languages} languages. Click any field to edit it in place.",
                },
                { languages: langs.length },
              ),
            },
            {
              anchor: "save.profile.cv",
              title: intl.formatMessage({ id: "profile.tour.cv.title", defaultMessage: "Your CV entries" }),
              description: intl.formatMessage(
                {
                  id: "profile.tour.cv.desc",
                  defaultMessage:
                    "{experiences} work experiences, {educations} educations, {skills} skills and {certificates} certificates. Add new entries here or click an existing one to edit it.",
                },
                {
                  experiences: experiences.length,
                  educations: educations.length,
                  skills: expertise.length,
                  certificates: certs.length,
                },
              ),
            },
            {
              anchor: "save.profile.powers",
              title: intl.formatMessage({ id: "profile.tour.powers.title", defaultMessage: "Your powers" }),
              description: intl.formatMessage({
                id: "profile.tour.powers.desc",
                defaultMessage:
                  "Your strongest powers at a glance. Click one to see its detail page, or assess more of them over in Grow.",
              }),
            },
            {
              anchor: "save.editVisibility",
              title: intl.formatMessage({ id: "profile.tour.visibility.title", defaultMessage: "Edit visibility" }),
              description: intl.formatMessage({
                id: "profile.tour.visibility.desc",
                defaultMessage:
                  "This toggle reveals a share switch next to every field and entry, so you decide exactly what other people can see of your profile.",
              }),
            },
            {
              anchor: "save.profile.service",
              title: intl.formatMessage({
                id: "profile.tour.service.title",
                defaultMessage: "Service menu",
              }),
              description: intl.formatMessage({
                id: "profile.tour.service.desc",
                defaultMessage:
                  "AI helpers and data services for your profile: extract powers and skills, import a CV or LinkedIn profile, generate a CV and export your data.",
              }),
            },
          ],
        }
      : null,
  );

  usePendingScope("save-profile", {
    dirty,
    save: async () => {
      if (!me) return;
      const personData = buildPersonData();
      const messages: string[] = [];

      // Unlike phone/email/etc., name has no sensible blank state - block the update.
      const nameEdit = personData?.name as { given: string[]; family: string | null } | undefined;
      const blankName = nameEdit != null && (nameEdit.given.length === 0 || !nameEdit.family);

      // Length/format/date guard for the edited base-data fields. genesis validates none of this on
      // the save path (`updatePerson` has no data validation and every column but `bio` is unbounded
      // `text`), so blocking here is the only thing standing between a malformed value and the DB.
      // Reveal every offending field's warning at once — the bar says only that something is wrong.
      const invalidFields = invalidBaseEdits(edits);
      setRevealFieldErrors(invalidFields.length > 0);

      // Validate before the first mutation so invalid base data cannot leave entries partially saved.
      if (blankName || invalidFields.length > 0) {
        throw new LocalizedError(
          blankName
            ? intl.formatMessage({
                id: "profile.page.error.nameRequired",
                defaultMessage: "First name and last name are required.",
              })
            : intl.formatMessage(
                {
                  id: "profile.page.error.invalidFields",
                  defaultMessage:
                    "{count, plural, one {One field needs attention} other {# fields need attention}} before saving.",
                },
                { count: invalidFields.length },
              ),
        );
      }

      const personalSharing = Object.keys(changedShared).length > 0 || allGroupsDirty ? {
        ...changedShared,
        allGroupsAttributes: [...new Set([...savedAllGroups, ...Object.keys(allGroupsEdits)])].filter(isAllGroups),
      } : undefined;
      const personOutcome = await (personData || personalSharing
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          person.update((personData ?? {}) as any, personalSharing).then(
            () => ({ ok: true as const }),
            (e: unknown) => ({
              ok: false as const,
              error: localizedMessageOf(e) ?? userErrorMessage(intl, e),
            }),
          )
        : Promise.resolve({ ok: true as const, skipped: true }));

      const entriesOutcome = await (entriesState.dirty
        ? entriesState.save().then(
            () => ({ ok: true as const }),
            (e: unknown) => ({
              ok: false as const,
              error: e instanceof Error ? e.message : intl.formatMessage({ id: "profile.page.error.saveEntries", defaultMessage: "Could not save entries" }),
            }),
          )
        : Promise.resolve({ ok: true as const, skipped: true }));

      const toplistsOutcome = await (toplistSharing.dirty
        ? toplistSharing.save().then(
            () => ({ ok: true as const }),
            (e: unknown) => ({ ok: false as const, error: e instanceof Error ? e.message : userErrorMessage(intl, e) }),
          )
        : Promise.resolve({ ok: true as const, skipped: true }));

      if (personOutcome.ok) {
        if (personalSharing) {
          setSharedEdits({});
          setAllGroupsEdits({});
          setProfileGroupMode(null);
          await sharingQuery.refetch();
        }
        if (personData) {
          setEdits({});
          setAvatarEdit(null);
        }
      } else {
        messages.push(personOutcome.error);
      }
      if (!entriesOutcome.ok) messages.push(entriesOutcome.error);
      if (!toplistsOutcome.ok) messages.push(toplistsOutcome.error);

      // Always refetch - the failing mutations may still have left rows
      // partially mutated, and the successful ones need their fresh values
      // surfaced before overlays clear from the UI.
      await refetch();
      if (avatarEdit && !viewingOther) await refetchOwnAvatar();

      if (messages.length > 0) {
        throw new LocalizedError(messages.join(" "));
      }
    },
    discard: () => {
      setRevealFieldErrors(false);
      setEdits({});
      setSharedEdits({});
      setAllGroupsEdits({});
      setProfileGroupMode(null);
      setAvatarEdit(null);
      setCropFile(null);
      entriesState.discard();
      toplistSharing.discard();
    },
  });

  // Without this the page rendered its empty shell (nameless header, every field blank), which
  // reads as a profile with no data. genesis returns nothing for "unknown" and "not shared"
  // alike, so one message has to cover both.
  // Keyed on "no person" rather than on person.error: usePerson also surfaces the unskipped
  // Myself query's error here, which would otherwise blank a foreign profile that loaded fine.
  if (!person.loading && !me) {
    // "Unknown or not shared" only makes sense for someone else's profile; a missing OWN
    // profile is a load or session failure, so it gets the reload/sign-in hint instead.
    const { anchorId, label, hint } =
      viewingOther && !person.error
        ? {
            anchorId: "profile-not-found",
            label: intl.formatMessage({ id: "profile.page.notFound", defaultMessage: "Profile not found" }),
            hint: intl.formatMessage({
              id: "profile.page.notFoundHint",
              defaultMessage: "This profile does not exist or has not been shared with you.",
            }),
          }
        : {
            anchorId: undefined,
            label: intl.formatMessage({
              id: "profile.page.loadFailed",
              defaultMessage: "Profile could not be loaded",
            }),
            hint: person.error
              ? userErrorMessage(intl, person.error)
              : intl.formatMessage({
                  id: "profile.page.loadFailedHint",
                  defaultMessage: "Please reload the page or sign in again.",
                }),
          };
    return (
      <div className="glass-section relative mt-8 max-w-[1600px] px-4 pb-12 @sm:px-6 @lg:px-10">
        <div className="mb-5">
          <BackButton />
        </div>
        <NoBackend id={anchorId} label={label} hint={hint} />
      </div>
    );
  }

  return (
    <EditContext.Provider value={canUpdateProfile}>
      <ProfileRevealContext.Provider value={revealFieldErrors}>
      <ShareEditContext.Provider value={{ mounted: shareMounted, exiting: shareMounted && !shareEditing }}>
        <div className="glass-section relative mt-8 max-w-[1600px] px-4 pb-12 @sm:px-6 @lg:px-10">
          <div className="relative">
            {shareEditing && sharingError && <p role="alert" className="text-smycles-body text-ink">{sharingError}</p>}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <BackButton />
                {canEdit ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      id="profile-visibility-toggle"
                      variant="toggle"
                      size="compact"
                      aria-pressed={shareEditing}
                      onClick={() => setShareEditing((v) => !v)}
                      data-assistant-anchor="save.editVisibility"
                    >
                      {shareEditing ? (
                        <FormattedMessage id="profile.page.hideVisibility" defaultMessage="Hide visibility" />
                      ) : (
                        <FormattedMessage id="profile.page.editVisibility" defaultMessage="Edit visibility" />
                      )}
                    </Button>

                  </div>
                ) : null}
                {canReadOrganizationalRecord && me?.id && <OrganizationalRecordToggle expanded={organizationalRecordExpanded} onOpen={openOrganizationalRecord} />}
              </div>
              {viewingOther && personIdParam ? (
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                  {alreadyPinned ? (
                    <Button
                      id="profile-inpins"
                      variant="secondary"
                      size="compact"
                      onClick={() => navigate("/save/pinboard")}
                    >
                      ✓ <FormattedMessage id="profile.page.inPins" defaultMessage="In pins" />
                    </Button>
                  ) : (
                    <Button
                      id="profile-addtopins"
                      variant="primary"
                      size="compact"
                      onClick={() => setAddTarget("pins")}
                      data-testid="add-to-pins-button"
                    >
                      + <FormattedMessage id="profile.page.addToPins" defaultMessage="Pins" />
                    </Button>
                  )}
                  {alreadyContact ? (
                    <Button
                      id="profile-incommunity"
                      variant="secondary"
                      size="compact"
                      onClick={() => navigate("/save/contact")}
                    >
                      ✓ <FormattedMessage id="profile.page.inCommunity" defaultMessage="In community" />
                    </Button>
                  ) : (
                    <Button
                      id="profile-addtocommunity"
                      variant="primary"
                      size="compact"
                      onClick={() => setAddTarget("community")}
                      data-testid="add-to-community-button"
                    >
                      + <FormattedMessage id="profile.page.addToCommunity" defaultMessage="Community" />
                    </Button>
                  )}
                  {/* The way into this person's Share page (spark#276). A link and not a
                      section on this page: their posts are the same grid the Share pillar
                      draws everywhere else, and the profile is already long.

                      Disabled rather than hidden when there is nothing to see: the button keeps
                      telling the reader that this person's posts are a thing that exists, and a
                      row of actions that silently changes length between profiles is harder to
                      read than one greyed-out entry. */}
                  <Button
                    variant="secondary"
                    size="compact"
                    disabled={!hasSharedItems}
                    onClick={() => navigate(`/share/person/${personIdParam}`)}
                    title={
                      hasSharedItems
                        ? undefined
                        : intl.formatMessage({
                            id: "profile.page.sharedItemsNone",
                            defaultMessage: "No posts to show",
                          })
                    }
                    data-testid="person-share-button"
                    className="uppercase tracking-[0.2em]"
                  >
                    <span className="inline-flex items-center gap-1">
                      <FormattedMessage id="profile.page.sharedItems" defaultMessage="Share" />
                      <span aria-hidden="true">↗</span>
                    </span>
                  </Button>
                  <MessageButton personId={personIdParam} />
                </div>
              ) : (
                /* Derselbe Weg auf dem eigenen Profil, nur zur eigenen Seite. Ohne ihn waere
                   "wo sehe ich, was jemand veroeffentlicht hat" eine Frage, die sich nur ueber
                   fremde Profile beantworten laesst, und das eigene waere der einzige Ort, an dem
                   der Weg fehlt. Nie ausgegraut: die eigene Seite hat auch leer etwas zu sagen,
                   naemlich die Aufforderung, etwas zu erstellen. */
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="compact"
                    onClick={() => navigate("/share/mine")}
                    data-testid="my-share-button"
                    className="uppercase tracking-[0.2em]"
                  >
                    <span className="inline-flex items-center gap-1">
                      <FormattedMessage id="profile.page.sharedItems" defaultMessage="Share" />
                      <span aria-hidden="true">↗</span>
                    </span>
                  </Button>
                  {canEdit ? (
                    <div data-assistant-anchor="save.profile.service">
                      <ServiceMenu />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            {canEdit && shareMounted && (
              <div className={`${shareEditing ? "slide-in-left" : "slide-out-left"} mb-5 flex flex-wrap items-center gap-4 rounded-lg border border-ink/10 p-4`}>
                <span className="text-smycles-body">
                  <FormattedMessage id="profile.sharing.entries" defaultMessage="Base setting:" />
                </span>
                {profileSharingPicker}
              </div>
            )}
            {canEdit ? <ProfileMergeBar personId={me?.id} expertiseBase={expertise.length} /> : null}
            {cropFile ? (
              <AvatarCropper
                file={cropFile}
                onCancel={() => setCropFile(null)}
                onApply={({ mimeType, content }) => {
                  setAvatarEdit({ mimeType, content });
                  setCropFile(null);
                }}
                onInvalid={(reason) => {
                  // Close first: the note lives under the avatar, and a message rendered behind an
                  // open modal is a message nobody reads.
                  setCropFile(null);
                  setAvatarError(
                    reason === "pixels"
                      ? intl.formatMessage(
                          {
                            id: "profile.page.avatar.tooManyPixels",
                            defaultMessage: "This image is too large to crop. Use one under {max} megapixels.",
                          },
                          { max: Math.round(MAX_AVATAR_PIXELS / 1_000_000) },
                        )
                      : intl.formatMessage({
                          id: "profile.page.avatar.notReadable",
                          defaultMessage: "That file could not be read as an image.",
                        }),
                  );
                }}
              />
            ) : null}
            <div className="mt-8 grid grid-cols-1 gap-6 @lg:grid-cols-[minmax(280px,0.5fr)_minmax(0,1fr)]">
              {/* Identity + Summary (left) */}
              <aside className="space-y-6">
                <div>
                  <div id="profile-business-card" className="box bg-surface p-6 text-center" data-assistant-anchor="save.profile.card">
                    <BoxHeader title={intl.formatMessage({ id: "profile.page.businessCard", defaultMessage: "Business card" })} className="text-left" />
                    <AvatarUpload
                      avatarUrl={avatarUrl}
                      name={profileName}
                      editable={canEdit}
                      hasAvatar={Boolean(avatarUrl)}
                      onSelectFile={(file) => {
                        // The picker's accept="image/*" only filters the dialog; a browser that lets
                        // the user override it, or a renamed file, still arrives here.
                        if (!file.type.startsWith("image/")) {
                          setAvatarError(
                            intl.formatMessage({
                              id: "profile.page.avatar.notAnImage",
                              defaultMessage: "Choose an image file.",
                            }),
                          );
                          return;
                        }
                        if (file.size > MAX_AVATAR_BYTES) {
                          setAvatarError(
                            intl.formatMessage(
                              {
                                id: "profile.page.avatar.tooLarge",
                                defaultMessage: "Choose an image of {max} MB or less.",
                              },
                              { max: Math.round(MAX_AVATAR_BYTES / MB) },
                            ),
                          );
                          return;
                        }
                        setAvatarError(null);
                        setCropFile(file);
                      }}
                      onRemove={() => setAvatarEdit({ remove: true })}
                    />
                    {avatarError ? (
                      <div id="profile-avatar-error" className="smycles-field-meta mt-1 text-ink-muted" data-testid="profile-avatar-error">
                        {avatarError}
                      </div>
                    ) : null}

                    {/* Given + family sit centred next to each other, each
                        hugging its content (no reserved clear-button gap). The
                        pair wraps to a second line when too wide; each field
                        also wraps internally (capped at the card width) so an
                        over-long single name can never spill past the box. */}
                    <div className="mt-4 flex flex-wrap items-baseline justify-center gap-x-2 font-serif text-smycles-display-m">
                      <InlineEdit
                        id="first-name-input"
                        value={profile.givenName}
                        onChange={update("givenName")}
                        editable={canUpdateProfile}
                        clearable={false}
                        wrap
                        placeholder={intl.formatMessage({ id: "profile.page.givenName.placeholder", defaultMessage: "First name" })}
                        className="font-serif text-smycles-display-m"
                        inputClassName="font-serif text-smycles-display-m"
                      />
                      <InlineEdit
                        id="last-name-input"
                        value={profile.familyName}
                        onChange={update("familyName")}
                        editable={canUpdateProfile}
                        clearable={false}
                        wrap
                        placeholder={intl.formatMessage({ id: "profile.page.familyName.placeholder", defaultMessage: "Last name" })}
                        className="font-serif text-smycles-display-m"
                        inputClassName="font-serif text-smycles-display-m"
                      />
                    </div>
                    <div className="mt-1 flex justify-center">
                      {/* Read-only mirror of jobTitle (Berufsbezeichnung); edited
                          in the Details → Company segment. Every other card field
                          is inline-editable, so a tooltip on the text explains
                          where this one is changed. Shown only when editable. */}
                      {(() => {
                        const roleSubtitle = (
                          <InlineEdit
                            value={profile.role}
                            onChange={update("role")}
                            editable={false}
                            className="text-smycles-label tracking-[0.15em] text-ink-muted"
                            inputClassName="text-smycles-label tracking-[0.15em] text-center"
                          />
                        );
                        return canEdit ? (
                          <Tooltip
                            content={intl.formatMessage({
                              id: "profile.page.businessCardRoleHint",
                              defaultMessage: "Taken from Details › Company › Job title",
                            })}
                          >
                            {roleSubtitle}
                          </Tooltip>
                        ) : (
                          roleSubtitle
                        );
                      })()}
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 text-left @sm:grid-cols-2">
                      <BasicField
                        id="phone-input"
                        type="tel"
                        label={intl.formatMessage({ id: "profile.page.label.phone", defaultMessage: "Phone" })}
                        value={profile.phone}
                        onChange={update("phone")}
                        shared={isBasicShared("phone")}
                        mergeKey="phone"
                      />
                      <BasicField
                        id="mail-input"
                        type="email"
                        label={intl.formatMessage({ id: "profile.page.label.email", defaultMessage: "Email" })}
                        value={profile.email}
                        onChange={update("email")}
                        shared={isBasicShared("email")}
                        mergeKey="email"
                      />
                      <SelectField
                        id="country-input"
                        label={intl.formatMessage({ id: "profile.page.label.country", defaultMessage: "Country" })}
                        value={profile.country}
                        onChange={(v) => {
                          update("country")(v);
                        }}
                        options={countryOptions}
                        shared={isBasicShared("country")}
                        mergeKey="country"
                        mergeFormat={(v) => formatCurrentForReview("country", v)}
                      />
                      <BasicField
                        id="city-input"
                        label={intl.formatMessage({ id: "profile.page.label.city", defaultMessage: "Place of residence" })}
                        value={profile.city}
                        onChange={update("city")}
                        shared={isBasicShared("city")}
                        mergeKey="city"
                      />
                    </div>
                  </div>
                </div>

                {/* Summary + Details + Languages presented as ONE tour stop (save.profile.about). */}
                <div className="space-y-6" data-assistant-anchor="save.profile.about">
                <div>
                  <div className="box smycles-field-value bg-surface px-5 py-4 leading-relaxed text-ink">
                    <BoxHeader
                      title={intl.formatMessage({ id: "profile.page.summary", defaultMessage: "Summary" })}
                      right={
                        <ShareToggle
                          id="summary-input-share"
                          shared={isBasicShared("summary")}
                        />
                      }
                    />
                    <InlineEdit
                      id="summary-input"
                      value={profile.summary}
                      onChange={(next) => {
                        summaryWarn.onEdit();
                        update("summary")(next);
                      }}
                      onCommit={summaryWarn.onBlur}
                      // Locked while LinkedIn proposes a change: the original
                      // keeps showing what is saved; editing happens on the
                      // compared pair in the merge card below.
                      editable={canUpdateProfile && !detailDiffFor(reviewPreview, "bio")}
                      multiline
                      className="block w-full"
                      inputClassName="smycles-field-value leading-relaxed text-ink min-h-[6rem]"
                    />
                    <MergeFieldWarning fieldKey="bio" error={summaryWarn.error} />
                    {canEdit ? <ProfileMergeField fieldKey="bio" multiline /> : null}
                  </div>
                </div>

                <div>
                  <div className="box bg-surface p-5">
                    <BoxHeader
                      title={intl.formatMessage({ id: "profile.page.details", defaultMessage: "Details" })}
                      right={
                        <SegmentedControl<"personal" | "company">
                          id="profile-details-mode"
                          size="sm"
                          value={detailsMode}
                          onChange={setDetailsMode}
                          options={[
                            { value: "personal", label: intl.formatMessage({ id: "profile.page.details.personal", defaultMessage: "Personal" }) },
                            { value: "company", label: intl.formatMessage({ id: "profile.page.details.company", defaultMessage: "Company" }) },
                          ]}
                        />
                      }
                    />
                    {detailsMode === "personal" ? (
                    <div className="grid grid-cols-1 gap-x-6 gap-y-4 @sm:grid-cols-2">
                      <SelectField
                        id="gender-input"
                        label={intl.formatMessage({ id: "profile.page.label.gender", defaultMessage: "Gender" })}
                        value={profile.gender}
                        onChange={(v) => {
                          update("gender")(v);
                        }}
                        options={genderOptions}
                        shared={isBasicShared("gender")}
                        mergeKey="gender"
                        mergeFormat={(v) => formatCurrentForReview("gender", v)}
                      />
                      <BasicField
                        id="birth-date-input"
                        label={intl.formatMessage({ id: "profile.page.label.birthday", defaultMessage: "Birthday" })}
                        value={profile.birthday}
                        onChange={update("birthday")}
                        type="date"
                        // Affordance only: the picker will not offer a year before this. Deliberately NOT
                        // a validation rule — neither genesis nor the product defines an earliest birth
                        // date, and entry dates legitimately reach back decades, so inventing a floor in
                        // dateFieldError would reject legitimate data and diverge from the import review.
                        min={BIRTH_DATE_PICKER_FLOOR}
                        suffix={profile.age ? intl.formatMessage({ id: "profile.page.ageSuffix", defaultMessage: "{age} years" }, { age: profile.age }) : null}
                        shared={isBasicShared("birthday")}
                        mergeKey="dateOfBirth"
                      />
                      <BasicField
                        id="nationality-input"
                        label={intl.formatMessage({ id: "profile.page.label.nationality", defaultMessage: "Nationality" })}
                        value={profile.nationality}
                        onChange={update("nationality")}
                        shared={isBasicShared("nationality")}
                        mergeKey="nationality"
                      />
                      <SelectField
                        id="marital-status-input"
                        label={intl.formatMessage({ id: "profile.page.label.maritalStatus", defaultMessage: "Marital status" })}
                        value={profile.maritalStatus}
                        onChange={(v) => {
                          update("maritalStatus")(v);
                        }}
                        options={maritalOptions}
                        shared={isBasicShared("maritalStatus")}
                        mergeKey="maritalStatus"
                        mergeFormat={(v) => formatCurrentForReview("maritalStatus", v)}
                      />
                      <BasicField
                        id="child-count-input"
                        label={intl.formatMessage({ id: "profile.page.label.childCount", defaultMessage: "Number of children" })}
                        value={profile.childCount}
                        onChange={update("childCount")}
                        type="number"
                        min={0}
                        max={20}
                        shared={isBasicShared("childCount")}
                        mergeKey="childCount"
                      />
                      <BasicField
                        id="website-input"
                        label={intl.formatMessage({ id: "profile.page.label.website", defaultMessage: "Website" })}
                        value={profile.website}
                        onChange={update("website")}
                        shared={isBasicShared("website")}
                        mergeKey="website"
                      />
                      <BasicField
                        id="profession-input"
                        label={intl.formatMessage({ id: "profile.page.label.profession", defaultMessage: "Profession" })}
                        value={profile.profession}
                        onChange={update("profession")}
                        shared={isBasicShared("profession")}
                        mergeKey="profession"
                      />
                      <BasicField
                        id="street-input"
                        label={intl.formatMessage({ id: "profile.page.label.street", defaultMessage: "Street" })}
                        value={profile.street}
                        onChange={update("street")}
                        shared={isBasicShared("street")}
                        mergeKey="street"
                      />
                      <BasicField
                        id="housenum-input"
                        label={intl.formatMessage({ id: "profile.page.label.houseNumber", defaultMessage: "House number" })}
                        value={profile.houseNumber}
                        onChange={update("houseNumber")}
                        shared={isBasicShared("houseNumber")}
                        mergeKey="houseNumber"
                      />
                      <BasicField
                        id="apartment-input"
                        label={intl.formatMessage({ id: "profile.page.label.apartment", defaultMessage: "Apartment" })}
                        value={profile.apartment}
                        onChange={update("apartment")}
                        shared={isBasicShared("apartment")}
                        mergeKey="apartment"
                      />
                      <BasicField
                        id="zipcode-input"
                        label={intl.formatMessage({ id: "profile.page.label.zipCode", defaultMessage: "Postal code" })}
                        value={profile.zipCode}
                        onChange={update("zipCode")}
                        shared={isBasicShared("zipCode")}
                        mergeKey="zipCode"
                      />
                      <SelectField
                        id="state-input"
                        label={intl.formatMessage({ id: "profile.page.label.countryState", defaultMessage: "State / region" })}
                        value={profile.countryState}
                        onChange={update("countryState")}
                        options={countryStateOptions}
                        shared={isBasicShared("countryState")}
                      />
                      <BasicField
                        id="driver-license-input"
                        label={intl.formatMessage({ id: "profile.page.label.drivingLicence", defaultMessage: "Driving licence" })}
                        value={profile.drivingLicence}
                        onChange={update("drivingLicence")}
                        shared={isBasicShared("drivingLicence")}
                        mergeKey="drivingLicence"
                      />
                      <div className="@sm:col-span-2">
                        <BasicField
                          id="misc-input"
                          label={intl.formatMessage({ id: "profile.page.label.miscellaneous", defaultMessage: "Other" })}
                          value={profile.miscellaneous}
                          onChange={update("miscellaneous")}
                          shared={isBasicShared("miscellaneous")}
                        />
                      </div>
                      <div className="@sm:col-span-2">
                        <MultiCountryField
                          id="citizenship-input"
                          label={intl.formatMessage({ id: "profile.page.label.citizenship", defaultMessage: "Citizenship" })}
                          value={profile.citizenship}
                          onChange={(v) => {
                            update("citizenship")(v);
                          }}
                          options={countryOptions}
                          shared={isBasicShared("citizenship")}
                          mergeKey="citizenship"
                          mergeFormat={(v) => formatCurrentForReview("citizenship", v)}
                        />
                      </div>
                    </div>
                    ) : (
                    <div className="grid grid-cols-1 gap-x-6 gap-y-4 @sm:grid-cols-2">
                      <BasicField
                        id="job-title-input"
                        label={intl.formatMessage({ id: "profile.page.label.jobTitle", defaultMessage: "Job title" })}
                        value={profile.role}
                        onChange={update("role")}
                        mergeKey="jobTitle"
                      />
                      <BasicField
                        id="employer-input"
                        label={intl.formatMessage({ id: "profile.page.label.currentEmployer", defaultMessage: "Employer" })}
                        value={profile.currentEmployer}
                        onChange={update("currentEmployer")}
                        shared={isBasicShared("currentEmployer")}
                        mergeKey="currentEmployer"
                      />
                      <SelectField
                        id="department-input"
                        label={intl.formatMessage({ id: "profile.page.label.jobDepartment", defaultMessage: "Department" })}
                        value={profile.jobDepartment}
                        onChange={update("jobDepartment")}
                        options={jobDepartmentOptions}
                        shared={false}
                      />
                      <SelectField
                        id="location-input"
                        label={intl.formatMessage({ id: "profile.page.label.jobLocation", defaultMessage: "Work location" })}
                        value={profile.jobLocation}
                        onChange={update("jobLocation")}
                        options={jobLocationOptions}
                        shared={false}
                      />
                      <SelectField
                        id="contracttype-input"
                        label={intl.formatMessage({ id: "profile.page.label.contractType", defaultMessage: "Contract type" })}
                        value={profile.contractType}
                        onChange={update("contractType")}
                        options={contractTypeOptions}
                        shared={isBasicShared("contractType")}
                      />
                      <SelectField
                        id="worktime-input"
                        label={intl.formatMessage({ id: "profile.page.label.workingTimeModel", defaultMessage: "Working time" })}
                        value={profile.workingTimeModel}
                        onChange={update("workingTimeModel")}
                        options={workingTimeModelOptions}
                        shared={isBasicShared("workingTimeModel")}
                      />
                      <BasicField
                        id="activity-input"
                        label={intl.formatMessage({ id: "profile.page.label.currentActivity", defaultMessage: "Current activity" })}
                        value={profile.currentActivity}
                        onChange={update("currentActivity")}
                        shared={isBasicShared("currentActivity")}
                        mergeKey="currentActivity"
                      />
                      <BasicField
                        id="currenttask-input"
                        label={intl.formatMessage({ id: "profile.page.label.currentTasks", defaultMessage: "Main tasks" })}
                        value={profile.currentTasks}
                        onChange={update("currentTasks")}
                        shared={isBasicShared("currentTasks")}
                      />
                    </div>
                    )}
                  </div>
                </div>

                <Section
                  id="profile-languages"
                  title={intl.formatMessage({ id: "profile.page.section.languages", defaultMessage: "Languages" })}
                  empty={langs.length === 0 && !sectionHasActionable(reviewPreview, "languageSkills")}
                  emptyLabel={intl.formatMessage({ id: "profile.page.empty.languages", defaultMessage: "No languages" })}
                  onAdd={canAddEntries ? (payload) => void addSectionDraft("language", langs, payload as Record<string, unknown>) : undefined}
                  addLabel={intl.formatMessage({ id: "profile.page.add.language", defaultMessage: "Add language" })}
                  addFields={languageFields}
                >
                  <div className="divide-y divide-ink/5">
                    {langs.map((l: LanguageEntry) => (
                      <LanguageRow
                        key={l.id}
                        entry={l}
                        editable={canEdit}
                        fields={languageFieldsFor(String((l as { languageTag?: unknown }).languageTag ?? ""))}
                        isDraft={draftIds.has(l.id)}
                        error={entryErrors[`language:${l.id}`]}
                        onPatch={(patch) => patchEntry("language", l.id, patch)}
                        onCancel={() => cancelEntry("language", l.id)}
                        onDelete={
                          draftIds.has(l.id) ? undefined : () => markForDelete("language", l.id)
                        }
                        shareControl={
                          shareEditing ? entryPicker([["language", [l]]],
                            `profile-language-${String(l.languageTag ?? "")}-share`) : null
                        }
                      />
                    ))}
                  </div>
                  {canEdit ? <ProfileMergeCollection section="languageSkills" /> : null}
                </Section>
                </div>
              </aside>

              {/* Career + Education (col 1+2) + Powers picker (col 3) */}
              <section className="grid grid-cols-1 items-start gap-6 @xl:grid-cols-[minmax(0,1.5fr)_minmax(260px,1fr)]">
                <div className="min-w-0 space-y-6" data-assistant-anchor="save.profile.cv">
                  <CareerGraph
                    experiences={experiences}
                    highlight={false}
                    title={sectionHeading("experience", experienceRows, sectionTitle.experience)}
                    emptyLabel={intl.formatMessage({ id: "profile.page.empty.experience", defaultMessage: "No work experience" })}
                    editable={canEdit}
                    onPatch={patchEntry}
                    onCancel={cancelEntry}
                    onDelete={markForDelete}
                    draftIds={draftIds}
                    editFields={experienceFields}
                    countryOptions={countryOptions}
                    industryOptions={industryOptions}
                    // Visibility is decided per box by the section master in the title; no control
                    // per role (the graph's text fallback is for consumers without such a master).
                    renderShareControl={() => null}
                    footer={
                      canAddEntries ? (
                        <>
                          {/* Imported experiences render INSIDE the work-experience box,
                              alongside the entries — not as a stray block below it. */}
                          <ProfileMergeCollection section="experiences" />
                          <AddForm
                            id="profile-experience-add"
                            variant={experiences.length === 0 ? "empty" : "row"}
                            label={intl.formatMessage({ id: "profile.page.add.experience", defaultMessage: "Add work experience" })}
                            onAdd={(payload) => void addSectionDraft("experience", experienceRows, payload)}
                            fields={experienceFields}
                            initiallyOpen={addKind === "experience"}
                            shareControl={false}
                          />
                        </>
                      ) : null
                    }
                  />

                  <Section
                    id="profile-education"
                    title={sectionHeading("education", educations, sectionTitle.education)}
                    empty={educations.length === 0 && !sectionHasActionable(reviewPreview, "educations")}
                    emptyLabel={intl.formatMessage({ id: "profile.page.empty.education", defaultMessage: "No education" })}
                    onAdd={canAddEntries ? (payload) => void addSectionDraft("education", educations, payload) : undefined}
                    addLabel={intl.formatMessage({ id: "profile.page.add.education", defaultMessage: "Add education" })}
                    addFields={educationFields}
                    addInitiallyOpen={addKind === "education"}
                    addShareControl={false}
                  >
                    <div className="divide-y divide-ink/5">
                      {educations.map((ed: EducationEntry) => (
                        <EducationRow
                          key={ed.id}
                          entry={ed}
                          editable={canEdit}
                          fields={educationFields}
                          countryOptions={countryOptions}
                          degreeOptions={degreeOptions}
                          isDraft={draftIds.has(ed.id)}
                          error={entryErrors[`education:${ed.id}`]}
                          onPatch={(patch) => patchEntry("education", ed.id, patch)}
                          onCancel={() => cancelEntry("education", ed.id)}
                          onDelete={
                            draftIds.has(ed.id) ? undefined : () => markForDelete("education", ed.id)
                          }
                        />
                      ))}
                    </div>
                    {canEdit ? (
                      <ProfileMergeCollection section="educations" degreeOptions={degreeOptions} />
                    ) : null}
                  </Section>

                  <Section
                    id="profile-expertise"
                    title={sectionHeading("expertise", expertise, sectionTitle.expertise)}
                    right={
                      expertise.length > 0 ? (
                        <SegmentedControl<"list" | "diagram">
                          id="skills-view"
                          size="sm"
                          value={skillsMode}
                          onChange={setSkillsMode}
                          options={[
                            { value: "list", label: intl.formatMessage({ id: "profile.skills.viewList", defaultMessage: "Classic" }) },
                            { value: "diagram", label: intl.formatMessage({ id: "profile.skills.viewDiagram", defaultMessage: "Modern" }) },
                          ]}
                        />
                      ) : undefined
                    }
                    empty={expertise.length === 0 && !sectionHasActionable(reviewPreview, "expertises")}
                    emptyLabel={intl.formatMessage({ id: "profile.page.empty.expertise", defaultMessage: "No skills" })}
                    onAdd={
                      canAddEntries && (expertise.length === 0 || skillsMode === "list")
                        ? (payload) => void addSectionDraft("expertise", expertise, payload)
                        : undefined
                    }
                    addLabel={intl.formatMessage({ id: "profile.page.add.expertise", defaultMessage: "Add skill" })}
                    addFields={expertiseFields}
                    addShareControl={false}
                  >
                    {skillsMode === "diagram" ? (
                      <SkillsDiagram
                        entries={expertise}
                        editable={canEdit}
                        onMove={
                          canEdit
                            ? (id, level) =>
                                patchEntry("expertise", id, { level: toStoragePercent(level) })
                            : undefined
                        }
                        onAdd={
                          canAddEntries ? (payload) => void addSectionDraft("expertise", expertise, payload) : undefined
                        }
                        onPatch={canEdit ? (id, patch) => patchEntry("expertise", id, patch) : undefined}
                        onCancel={canEdit ? (id) => cancelEntry("expertise", id) : undefined}
                        onDelete={canEdit ? (id) => markForDelete("expertise", id) : undefined}
                        editFields={expertiseFields}
                        draftIds={draftIds}
                      />
                    ) : (
                      <div className="divide-y divide-ink/5">
                        {expertise.map((e: ExpertiseEntry) => (
                          <ExpertiseRow
                            key={e.id}
                            entry={e}
                            editable={canEdit}
                            fields={expertiseFields}
                            isDraft={draftIds.has(e.id)}
                            error={entryErrors[`expertise:${e.id}`]}
                            onPatch={(patch) => patchEntry("expertise", e.id, patch)}
                            onCancel={() => cancelEntry("expertise", e.id)}
                            onDelete={
                              draftIds.has(e.id) ? undefined : () => markForDelete("expertise", e.id)
                            }
                          />
                        ))}
                      </div>
                    )}
                    {canEdit ? <ProfileMergeSkills /> : null}
                  </Section>

                  <Section
                    id="profile-certificates"
                    title={sectionHeading("certificate", certs, sectionTitle.certificates)}
                    empty={certs.length === 0 && !sectionHasActionable(reviewPreview, "certificates")}
                    emptyLabel={intl.formatMessage({ id: "profile.page.empty.certificates", defaultMessage: "No certificates" })}
                    onAdd={canAddEntries ? (payload) => void addSectionDraft("certificate", certs, payload) : undefined}
                    addLabel={intl.formatMessage({ id: "profile.page.add.certificate", defaultMessage: "Add certificate" })}
                    addFields={certificateFields}
                    addInitiallyOpen={addKind === "certificate"}
                    addShareControl={false}
                  >
                    <div className="divide-y divide-ink/5">
                      {certs.map((c: CertificateEntry) => (
                        <CertificateRow
                          key={c.id}
                          entry={c}
                          editable={canEdit}
                          fields={certificateFields}
                          isDraft={draftIds.has(c.id)}
                          error={entryErrors[`certificate:${c.id}`]}
                          onPatch={(patch) => patchEntry("certificate", c.id, patch)}
                          onCancel={() => cancelEntry("certificate", c.id)}
                          onDelete={
                            draftIds.has(c.id) ? undefined : () => markForDelete("certificate", c.id)
                          }
                        />
                      ))}
                    </div>
                    {canEdit ? <ProfileMergeCollection section="certificates" /> : null}
                  </Section>
                </div>

                <aside className="space-y-6">
                  <div data-assistant-anchor="save.profile.powers">
                    <PowersSidebar
                      onNavigate={(path) => navigate(path)}
                      showAllLink={!viewingOther}
                      levels={foreignPowerLevels}
                      smartLevels={foreignSmartLevels}
                      shared={powersShared}

                      personName={profileName}
                      scoreSources={scoreSources}
                    />
                  </div>
                  {/* Own lists come with their sharing; another person's only as far as
                      they shared them, and the box leaves itself out when that is none. */}
                  {viewingOther ? (
                    <FavoritesSidebar toplists={me && "toplists" in me ? (me.toplists ?? []) : []} />
                  ) : (
                    <FavoritesSidebar
                      toplists={toplistSharing.toplists}
                      loading={toplistSharing.loading}
                      error={toplistSharing.error}
                      own={{ onNavigate: (path) => navigate(path), sharing: toplistSharing.control, remove: toplistSharing.remove, removalErrors: toplistSharing.removalErrors }}
                    />
                  )}
                </aside>
              </section>
            </div>
          </div>
          {me?.id && <OrganizationalRecordSection expanded={organizationalRecordExpanded} sectionRef={organizationalRecordSectionRef}
            subject={{ kind: "person", id: me.id }} access={organizationalRecordAccess} />}
        </div>
        {viewingOther && personIdParam ? (
          <AddToCollectionDialog
            key={addTarget ?? "closed"}
            id="profile-addtocollection"
            open={addTarget !== null}
            target={addTarget ?? "pins"}
            onClose={() => setAddTarget(null)}
            contactIds={[personIdParam]}
            personName={profileName}
          />
        ) : null}
      </ShareEditContext.Provider>
      </ProfileRevealContext.Provider>
    </EditContext.Provider>
  );
}

// === Utilities (mirror workshop) ===

function fullName(name: { given?: readonly string[] | null; family?: string | null } | null | undefined): string {
  if (!name) return "";
  const givenStr = name.given?.filter(Boolean).join(" ") ?? "";
  return [givenStr, name.family].filter(Boolean).join(" ");
}

// Maps a single edited field to the UpdatePersonDataInput shape. Relations
// wrap as { id }, multi-country joins to [{ id }], scalars pass through.
